'use client'

import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react'
import { downloadThemeTarball } from '@/lib/api'
import { useFileStore } from '@/lib/file-store'
import { toast } from 'sonner'
import consumer from '@/lib/cable';

interface ThemeActivationModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  theme: {
    id: number
    name: string
    theme_type: string
  } | null
  siteId: string
  onActivationComplete?: () => void
}

export function ThemeActivationModal({
  open,
  onOpenChange,
  theme,
  siteId,
  onActivationComplete,
}: ThemeActivationModalProps) {
  const [isActivating, setIsActivating] = useState(false)
  const [progress, setProgress] = useState(0)
  const [status, setStatus] = useState<'idle' | 'downloading' | 'complete' | 'error'>('idle')
  const [progressMessages, setProgressMessages] = useState<string[]>([])
  const { setFiles, addFile } = useFileStore()


  // Subscribe to Action Cable for progress updates
  useEffect(() => {
    console.log('ThemeActivationModal useEffect triggered:', { open, isActivating })
    
    if (!open) return // || !isActivating) return

    console.log('Subscribing to ThemeActivationChannel for site:', siteId)
    // IMPORTANT: ActionCable only takes 2 args here (identifier, callbacks).
    // If callbacks are split into multiple objects, only the 2nd arg is used.
    const channel = consumer.subscriptions.create(
      {
        channel: 'ThemeActivationChannel',
        // Backend streams by site slug (ThemeTarballDownloadJob uses site.slug)
        site_slug: siteId,
      },
      {
        connected() {
          console.log('Connected to ThemeActivationChannel')
        },

        disconnected() {
          console.log('Disconnected from ThemeActivationChannel')
        },

        rejected() {
          console.warn('ThemeActivationChannel subscription rejected')
        },

        received(data: any) {
          console.log('Received theme activation data:', data)

          if (data.type === 'progress') {
            setProgressMessages((prev) => [...prev, data.message])

            if (data.status === 'error') {
              setStatus('error')
              setIsActivating(false)
              toast.error(data.message)
              return
            }

            if (data.status === 'complete') {
              setStatus('complete')
              setProgress(100)
              setIsActivating(false)
              return
            }

            // Increment progress for each message
            setProgress((prev) => Math.min(prev + 10, 90))
            return
          }

          if (data.type === 'files') {
            // Load files into workspace
            setStatus('complete')
            setProgress(100)
            setProgressMessages((prev) => [...prev, `Loading ${data.files.length} files into workspace...`])

            // Clear existing files and load theme files
            setFiles([])
            data.files.forEach((file: { path: string; content: string }) => {
              addFile(file.path, file.content)
            })

            setProgressMessages((prev) => [...prev, 'Theme files loaded successfully!'])
            toast.success(`Theme "${data.theme.name}" activated successfully!`)

            setTimeout(() => {
              setIsActivating(false)
              onActivationComplete?.()
              onOpenChange(false)
            }, 2000)
          }
        },
      }
    )

    return () => {
      channel.unsubscribe()
    }
    
  }, [open, siteId, addFile, onActivationComplete, onOpenChange, setFiles])

  const handleActivate = async () => {
    if (!theme) return

    setIsActivating(true)
    setStatus('downloading')
    setProgress(0)
    setProgressMessages([])

    try {
      const response = await downloadThemeTarball(siteId, theme.id.toString())
      setProgressMessages([response.message])
    } catch (error: any) {
      setStatus('error')
      setIsActivating(false)
      toast.error(error.message || 'Failed to start theme download')
    }
  }

  const handleClose = () => {
    if (!isActivating) {
      setStatus('idle')
      setProgress(0)
      setProgressMessages([])
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Activate Theme</DialogTitle>
          <DialogDescription>
            {status === 'idle' && theme && (
              <>
                This will download and activate the <strong>{theme.name}</strong> theme.
                All current files will be replaced with the theme files.
              </>
            )}
            {status === 'downloading' && 'Downloading and extracting theme files...'}
            {status === 'complete' && 'Theme activated successfully!'}
            {status === 'error' && 'An error occurred while activating the theme.'}
          </DialogDescription>
        </DialogHeader>

        {isActivating && (
          <div className="space-y-4 py-4">
            <Progress value={progress} className="w-full" />
            <div className="max-h-48 overflow-y-auto space-y-2">
              {progressMessages.map((message, index) => (
                <div key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                  {status === 'error' ? (
                    <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                  ) : status === 'complete' && index === progressMessages.length - 1 ? (
                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                  ) : (
                    <Loader2 className="h-4 w-4 animate-spin flex-shrink-0 mt-0.5" />
                  )}
                  <span>{message}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {status === 'complete' && (
          <div className="py-4 flex items-center justify-center text-green-500">
            <CheckCircle className="h-8 w-8 mr-2" />
            <span className="font-medium">Theme activated successfully!</span>
          </div>
        )}

        <DialogFooter>
          {status === 'idle' && (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleActivate} disabled={isActivating}>
                Activate Theme
              </Button>
            </>
          )}
          {(status === 'complete' || status === 'error') && (
            <Button onClick={handleClose}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
